import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useRascunho, type ConfigRascunho } from '../useRascunho';
import {
  combinarDataHora,
  draftDeEvento,
  eventoDraftVazio,
  slugificar,
} from '@/pages/assistente-evento/utils';
import type { EventoDraft, ImagemDraft } from '@/pages/assistente-evento/types';

const CHAVE_RASCUNHO = 'rascunho:divulgar';
const CATEGORIA_PADRAO = 'outro';

export interface ItemPendencia {
  chave: string;
  rotulo: string;
  pronta: boolean;
}

export interface ItemMelhoria {
  chave: string;
  rotulo: string;
  icone: string;
  ajuda?: string;
  pronta: boolean;
}

async function garantirSlug(
  nome: string,
  slugAtual: string,
  eventoIdAtual: number | null,
): Promise<string> {
  const existente = slugAtual.trim();
  if (existente) return existente;
  const base = slugificar(nome) || 'evento';
  let candidato = base;
  let sufixo = 2;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    let q = supabase.from('eventos').select('id').eq('slug', candidato);
    if (eventoIdAtual) q = q.neq('id', eventoIdAtual);
    const { data } = await q.maybeSingle();
    if (!data) return candidato;
    candidato = `${base}-${sufixo}`;
    sufixo += 1;
  }
}

async function processarArte(
  imagem: ImagemDraft,
  eventoIdNum: number,
): Promise<string | null | undefined> {
  if (imagem.arquivo) {
    const nome = imagem.arquivo.name || 'arte.jpg';
    const ext = nome.split('.').pop()?.toLowerCase() || 'jpg';
    const caminho = `${eventoIdNum}/arte-${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from('capas').upload(caminho, imagem.arquivo);
    if (error) throw error;
    const { data } = supabase.storage.from('capas').getPublicUrl(caminho);
    return data.publicUrl;
  }
  if (imagem.removida) return null;
  return undefined;
}

export function useDivulgar() {
  const slugRef = useRef('');
  const mudarDraftRef = useRef<(patch: Partial<EventoDraft>) => void>(() => {});
  const [perfilCidade, setPerfilCidade] = useState('');
  const [perfilUf, setPerfilUf] = useState('');

  const config: ConfigRascunho<EventoDraft> = {
    chaveLocal: CHAVE_RASCUNHO,
    tabela: 'eventos',
    rotaVoltar: '/criar/divulgar',
    draftInicial: { ...eventoDraftVazio(), categoria: CATEGORIA_PADRAO },
    retomar: async (id, userId) => {
      const { data: evRes } = await supabase
        .from('eventos')
        .select('*')
        .eq('id', id)
        .eq('dono_id', userId)
        .eq('status', 'rascunho')
        .maybeSingle();
      if (!evRes) return null;
      slugRef.current = (evRes.slug as string) || '';
      return {
        atualizado_em: (evRes.atualizado_em as string) || null,
        draft: draftDeEvento(evRes as Record<string, unknown>),
      };
    },
    paraColunas: async (d, id) => {
      const slug = await garantirSlug(d.nome, d.slug, id);
      slugRef.current = slug;
      const inicio = combinarDataHora(d.inicioData, d.inicioHora);
      const fimCalc = combinarDataHora(d.fimData, d.fimHora);
      const fim =
        fimCalc ||
        (inicio ? new Date(new Date(inicio).getTime() + 3 * 3600 * 1000).toISOString() : null);
      return {
        nome: d.nome.trim(),
        slug,
        descricao: d.descricao || null,
        categoria: d.categoria || CATEGORIA_PADRAO,
        local_nome: d.localNome || null,
        endereco: d.endereco || null,
        cidade: d.cidade.trim(),
        uf: d.uf,
        cep: d.cep || null,
        inicio_em: inicio,
        fim_em: fim,
      };
    },
    criar: async (colunas) => {
      const { data: novoId, error } = await supabase.rpc('criar_evento', {
        p_nome: colunas.nome,
        p_slug: colunas.slug,
        p_descricao: colunas.descricao || '',
        p_categoria: colunas.categoria,
        p_local_nome: colunas.local_nome || '',
        p_endereco: colunas.endereco || '',
        p_cidade: colunas.cidade,
        p_uf: colunas.uf,
        p_cep: colunas.cep || '',
        p_inicio_em: colunas.inicio_em,
        p_fim_em: colunas.fim_em,
      });
      if (error) throw error;
      const id = Number(novoId);
      await supabase.from('eventos').update({ vende_ingressos: false }).eq('id', id);
      await supabase.rpc('definir_usa_equipes', { p_evento: id, p_valor: false });
      return id;
    },
    aposSalvar: async (id, d) => {
      const url = await processarArte(d.imagemDesktop, id);
      if (url === undefined) return;
      await supabase
        .from('eventos')
        .update({ imagem_desktop_url: url, imagem_mobile_url: url })
        .eq('id', id);
      mudarDraftRef.current({
        imagemDesktop: { arquivo: null, url, removida: false, carregando: false },
        imagemMobile: { arquivo: null, url, removida: false, carregando: false },
      });
    },
    podeSalvar: (d) => !!(d.nome.trim() && d.inicioData && d.inicioHora),
    publicarRegistro: async (id) => {
      const { error } = await supabase
        .from('eventos')
        .update({
          categoria: CATEGORIA_PADRAO,
          financeiro_ativo: false,
          status: 'publicado',
          publicado_em: new Date().toISOString(),
        })
        .eq('id', id);
      if (error) throw error;
    },
    destinoPublicado: () => `/e/${slugRef.current}`,
  };

  const motor = useRascunho(config);
  const { draft, mudarDraft } = motor;
  mudarDraftRef.current = mudarDraft;

  // Cidade/UF pré-preenchidos do perfil (só quando não retomou rascunho).
  useEffect(() => {
    if (motor.estado !== 'ok') return;
    let ativo = true;

    async function carregarPerfil() {
      const { data: perfilData } = await supabase
        .from('vw_meu_perfil')
        .select('cidade, uf')
        .maybeSingle();
      const pCidade = ((perfilData as { cidade?: string | null } | null)?.cidade) || '';
      const pUf = ((perfilData as { uf?: string | null } | null)?.uf) || '';
      if (ativo) {
        setPerfilCidade(pCidade);
        setPerfilUf(pUf);
        mudarDraft({
          categoria: CATEGORIA_PADRAO,
          formaParticipar: 'inscricao',
          cidade: draft.cidade || pCidade,
          uf: draft.uf || pUf,
        });
      }
    }

    carregarPerfil();
    return () => {
      ativo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [motor.estado]);

  const pendencias: ItemPendencia[] = [
    { chave: 'nome', rotulo: 'Nome', pronta: !!draft.nome.trim() },
    {
      chave: 'inicioData',
      rotulo: 'Data de início',
      pronta: !!(draft.inicioData && draft.inicioHora),
    },
    { chave: 'cidade', rotulo: 'Cidade e UF', pronta: !!(draft.cidade.trim() && draft.uf) },
  ];

  const melhorias: ItemMelhoria[] = [
    {
      chave: 'arte',
      rotulo: 'A arte de divulgação',
      icone: 'ri-image-line',
      ajuda: 'Eventos com arte recebem muito mais visitas.',
      pronta: !!draft.imagemDesktop.url,
    },
    {
      chave: 'descricao',
      rotulo: 'Uma descrição',
      icone: 'ri-file-text-line',
      pronta: !!draft.descricao.trim(),
    },
    {
      chave: 'endereco',
      rotulo: 'O endereço completo',
      icone: 'ri-map-pin-line',
      pronta: !!draft.endereco.trim(),
    },
  ];

  return {
    estado: motor.estado,
    erro: motor.erro,
    eventoId: motor.registroId,
    draft,
    mudarDraft,
    perfilCidade,
    perfilUf,
    estadoSalvamento: motor.estadoSalvamento,
    salvando: motor.salvando,
    publicar: motor.publicar,
    setErro: motor.setErro,
    pendencias,
    melhorias,
  };
}